import classNames from 'classnames';
import { ReactNode, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import useResizeObserver from '../../hooks/useResizeObserver';
import styles from './styles/styles.module.scss';

export type VerticalSlideProps = {
  id?: string;
  children: ReactNode;
  className?: string;
  ignoreInitialOpeningAnimation?: boolean; // Used to have something start "open" without sliding open
  isOpen: boolean;
  onTransitionEnd?: (isOpen: boolean) => void;
};

export default function VerticalSlide({
  id,
  children,
  className,
  ignoreInitialOpeningAnimation,
  isOpen,
  onTransitionEnd
}: VerticalSlideProps) {
  const [hasRendered, setHasRendered] = useState(false);
  const [isTabbable, setIsTabbable] = useState(false);
  const [maxHeight, setMaxHeight] = useState(0);

  const innerContainer = useRef<HTMLDivElement>(null);

  const resizeHeight = useCallback(entry => {
    const { height } = entry[0].target.getBoundingClientRect();
    setMaxHeight(height);
  }, []);
  const [observe, disconnect] = useResizeObserver(resizeHeight);

  useEffect(() => {
    observe(innerContainer.current);
    return disconnect;
  }, [observe, disconnect]);

  const style = useMemo(() => {
    const innerHeight = isOpen && innerContainer.current ? maxHeight : 0;
    if (isOpen) {
      setIsTabbable(isOpen);

      if (ignoreInitialOpeningAnimation && !hasRendered) {
        return { maxHeight: innerHeight, transition: 'none' };
      }
    }

    setHasRendered(true);

    return { maxHeight: innerHeight };

    // the first and only update to hasRendered doesn't need to trigger this again
  }, [ignoreInitialOpeningAnimation, isOpen, maxHeight]);

  const handleTransitionEnd = () => {
    setIsTabbable(isOpen);
    onTransitionEnd?.(isOpen);
  };

  return (
    <div
      id={id}
      hidden={!isTabbable}
      style={style}
      className={classNames(styles.container, { [styles.visible]: isTabbable }, className)}
      onTransitionEnd={handleTransitionEnd}
    >
      <div ref={innerContainer}>{children}</div>
    </div>
  );
}
